import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Eye, EyeOff, Pencil, Trash2, Users } from 'lucide-react';
import {
  apiClient,
  type AllowedModule,
  type ApiError,
  type InviteResponse,
  type MembershipWithUserResponse,
} from '@/lib/api-client';
import { useAuthStore } from '@/lib/auth-store';
import { Button } from '@/components/ui/button';
import { ConfigModal } from './config-modal';
import { VisibilityGrantsSection } from './visibility-grants-section';

// ---------------------------------------------------------------------------
// Constantes
// ---------------------------------------------------------------------------

const MODULE_LABELS: Record<AllowedModule, string> = {
  clients: 'Clientes',
  cases: 'Processos',
  publications: 'Intimações',
  agenda: 'Agenda e tarefas',
  documents: 'Documentos',
  financial: 'Financeiro',
  reports: 'Relatórios',
  assistant: 'Assistente de IA',
};

// Precisa espelhar VALID_MODULES do backend. 'assistant' estava faltando: o
// backend já aceitava o valor, mas a UI não oferecia, então nenhum member
// conseguia receber o módulo e qualquer edição de usuário o apagava da lista.
const ALL_MODULES: AllowedModule[] = [
  'clients',
  'cases',
  'publications',
  'agenda',
  'documents',
  'financial',
  'reports',
  'assistant',
];

const ROLE_LABEL: Record<string, string> = {
  owner: 'Proprietário',
  admin: 'Administrador',
  lawyer: 'Advogado',
  assistant: 'Assistente',
  financial: 'Financeiro',
  // Mantido só para exibir linhas legadas. O rótulo antigo era "Visualizador",
  // o que passava a ideia errada de acesso restrito funcionando. O backend
  // agora nega acesso a esse role. Ver docs/PORTAL-CLIENTE.md.
  client_read_only: 'Acesso revogado (legado)',
};

function roleBadge(m: MembershipWithUserResponse) {
  if (m.legacyRole === 'owner') {
    return (
      <span className="inline-flex items-center rounded-full bg-amber-500/15 px-2 py-0.5 text-xs font-medium text-amber-500">
        Proprietário
      </span>
    );
  }
  if (m.isAdmin) {
    return (
      <span className="inline-flex items-center rounded-full bg-violet-500/15 px-2 py-0.5 text-xs font-medium text-violet-400">
        Administrador
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-sky-500/15 px-2 py-0.5 text-xs font-medium text-sky-400">
      {ROLE_LABEL[m.legacyRole] ?? m.legacyRole}
    </span>
  );
}

function moduleChips(m: MembershipWithUserResponse) {
  if (m.legacyRole === 'owner' || m.isAdmin) {
    return (
      <span className="text-xs text-[var(--fg-muted)] italic">Todos</span>
    );
  }
  if (!m.allowedModules || m.allowedModules.length === 0) {
    return <span className="text-xs text-[var(--fg-muted)] italic">Nenhum</span>;
  }
  return (
    <div className="flex flex-wrap gap-1">
      {m.allowedModules.map((mod) => (
        <span
          key={mod}
          className="inline-flex items-center rounded-full bg-[color-mix(in_oklch,var(--fg)_8%,transparent)] px-2 py-0.5 text-[11px] text-[var(--fg-muted)]"
        >
          {MODULE_LABELS[mod]}
        </span>
      ))}
    </div>
  );
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  const first = parts[0] ?? '';
  if (parts.length === 1) return first.slice(0, 2).toUpperCase();
  const last = parts[parts.length - 1] ?? '';
  return ((first[0] ?? '') + (last[0] ?? '')).toUpperCase();
}

// ---------------------------------------------------------------------------
// Formulário (criar / editar)
// ---------------------------------------------------------------------------

interface UserFormProps {
  open: boolean;
  onClose: () => void;
  editing: MembershipWithUserResponse | null;
  tenantId: string;
  allMembers: MembershipWithUserResponse[];
  onDone: () => void;
}

function UserFormModal({ open, onClose, editing, tenantId, allMembers, onDone }: UserFormProps) {
  const isNew = !editing;

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [modules, setModules] = useState<AllowedModule[]>([...ALL_MODULES]);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Sincroniza estado quando modal abre
  function handleOpen() {
    setError(null);
    setSubmitting(false);
    if (editing) {
      setIsAdmin(editing.isAdmin);
      setModules(editing.allowedModules ?? [...ALL_MODULES]);
    } else {
      setName('');
      setEmail('');
      setPassword('');
      setShowPwd(false);
      setIsAdmin(false);
      setModules([...ALL_MODULES]);
    }
  }

  // Chamado via useEffect no open
  const [prevOpen, setPrevOpen] = useState(false);
  if (open && !prevOpen) {
    setPrevOpen(true);
    handleOpen();
  }
  if (!open && prevOpen) {
    setPrevOpen(false);
  }

  function toggleModule(mod: AllowedModule) {
    setModules((prev) =>
      prev.includes(mod) ? prev.filter((m) => m !== mod) : [...prev, mod],
    );
  }

  async function submit() {
    setError(null);
    if (isNew) {
      if (!name.trim()) { setError('Nome obrigatório'); return; }
      if (!email.trim()) { setError('E-mail obrigatório'); return; }
      if (password.length < 8) { setError('Senha deve ter pelo menos 8 caracteres'); return; }
    }
    setSubmitting(true);
    try {
      if (isNew) {
        await apiClient.tenantsCreateUser(tenantId, {
          name: name.trim(),
          email: email.trim(),
          password,
          isAdmin,
          allowedModules: isAdmin ? undefined : modules,
        });
      } else {
        await apiClient.tenantsUpdateMember(tenantId, editing!.userId, {
          isAdmin,
          allowedModules: isAdmin ? undefined : modules,
        });
      }
      onDone();
      onClose();
    } catch (e) {
      setError((e as ApiError).message);
    } finally {
      setSubmitting(false);
    }
  }

  const isOwner = editing?.legacyRole === 'owner';

  return (
    <ConfigModal
      open={open}
      onClose={onClose}
      title={isNew ? 'Novo usuário' : 'Editar usuário'}
      description={isNew ? 'Cria um usuário e o adiciona ao escritório.' : 'Altera papel e módulos do membro.'}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button onClick={submit} disabled={submitting || isOwner}>
            {submitting ? 'Salvando…' : 'Salvar'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error && (
          <p className="rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-500">
            {error}
          </p>
        )}

        {isOwner && (
          <p className="rounded-md bg-amber-500/10 px-3 py-2 text-xs text-amber-500">
            O papel de Proprietário não pode ser editado.
          </p>
        )}

        {isNew && (
          <>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-[var(--fg-muted)]">
                Nome <span className="text-red-500">*</span>
              </label>
              <input
                className="h-9 w-full rounded-md border border-[var(--border)] bg-transparent px-3 text-sm text-[var(--fg)] placeholder:text-[var(--fg-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Nome completo"
                disabled={submitting}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-[var(--fg-muted)]">
                E-mail <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                className="h-9 w-full rounded-md border border-[var(--border)] bg-transparent px-3 text-sm text-[var(--fg)] placeholder:text-[var(--fg-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="email@exemplo.com"
                disabled={submitting}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-[var(--fg-muted)]">
                Senha <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type={showPwd ? 'text' : 'password'}
                  className="h-9 w-full rounded-md border border-[var(--border)] bg-transparent px-3 pr-9 text-sm text-[var(--fg)] placeholder:text-[var(--fg-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Mínimo 8 caracteres"
                  disabled={submitting}
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--fg-muted)] hover:text-[var(--fg)]"
                  tabIndex={-1}
                >
                  {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
          </>
        )}

        <label className="flex items-center gap-2 text-sm text-[var(--fg)]">
          <input
            type="checkbox"
            checked={isAdmin}
            onChange={(e) => setIsAdmin(e.target.checked)}
            disabled={submitting || isOwner}
            className="h-4 w-4 rounded border-[var(--border)] accent-[var(--accent)]"
          />
          Administrador
          <span className="text-xs text-[var(--fg-muted)]">(acessa todos os módulos)</span>
        </label>

        {!isAdmin && !isOwner && (
          <div className="flex flex-col gap-2">
            <p className="text-xs font-medium text-[var(--fg-muted)]">Módulos permitidos</p>
            <div className="grid grid-cols-2 gap-1.5">
              {ALL_MODULES.map((mod) => (
                <label key={mod} className="flex items-center gap-2 text-sm text-[var(--fg)]">
                  <input
                    type="checkbox"
                    checked={modules.includes(mod)}
                    onChange={() => toggleModule(mod)}
                    disabled={submitting}
                    className="h-4 w-4 rounded border-[var(--border)] accent-[var(--accent)]"
                  />
                  {MODULE_LABELS[mod]}
                </label>
              ))}
            </div>
          </div>
        )}

        {editing && !isOwner && (
          <VisibilityGrantsSection
            viewer={editing}
            otherMembers={allMembers.filter((m) => m.userId !== editing.userId)}
          />
        )}
      </div>
    </ConfigModal>
  );
}

// ---------------------------------------------------------------------------
// Página principal
// ---------------------------------------------------------------------------

export function UsersPage() {
  const tenantId = useAuthStore((s) => s.tenantId);
  const queryClient = useQueryClient();

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<MembershipWithUserResponse | null>(null);
  const [toast, setToast] = useState<{ msg: string; type: 'ok' | 'err' } | null>(null);

  function showToast(msg: string, type: 'ok' | 'err' = 'ok') {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  }

  const { data: members, isLoading: loadingMembers, error: membersError } = useQuery({
    queryKey: ['tenants', tenantId, 'members'],
    queryFn: () => apiClient.tenantsListMembers(tenantId!),
    enabled: !!tenantId,
  });

  const { data: invites } = useQuery({
    queryKey: ['tenants', tenantId, 'invites'],
    queryFn: () => apiClient.tenantsListInvites(tenantId!),
    enabled: !!tenantId,
  });

  const removeMut = useMutation({
    mutationFn: (userId: string) => apiClient.tenantsRemoveMember(tenantId!, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants', tenantId, 'members'] });
      showToast('Membro removido');
    },
    onError: (e: ApiError) => showToast(e.message, 'err'),
  });

  const revokeInviteMut = useMutation({
    mutationFn: (inviteId: string) => apiClient.tenantsRevokeInvite(tenantId!, inviteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants', tenantId, 'invites'] });
      showToast('Convite revogado');
    },
    onError: (e: ApiError) => showToast(e.message, 'err'),
  });

  function openNew() {
    setEditing(null);
    setModalOpen(true);
  }

  function openEdit(m: MembershipWithUserResponse) {
    if (m.legacyRole === 'owner') return;
    setEditing(m);
    setModalOpen(true);
  }

  function handleRemove(m: MembershipWithUserResponse) {
    if (!window.confirm(`Remover ${m.user.name} do escritório?`)) return;
    removeMut.mutate(m.userId);
  }

  function handleRevokeInvite(inv: InviteResponse) {
    if (!window.confirm(`Revogar convite para ${inv.email}?`)) return;
    revokeInviteMut.mutate(inv.id);
  }

  const pendingInvites = (invites ?? []).filter((i) => !i.acceptedAt);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-[var(--fg)]">Usuários</h1>
          <p className="mt-0.5 text-sm text-[var(--fg-muted)]">
            Gerencie membros e permissões de acesso ao escritório.
          </p>
        </div>
        <Button onClick={openNew}>Novo usuário</Button>
      </div>

      {/* Toast */}
      {toast && (
        <div
          className={`fixed bottom-6 right-6 z-[80] rounded-lg px-4 py-3 text-sm shadow-lg ${
            toast.type === 'ok'
              ? 'bg-emerald-600 text-white'
              : 'bg-red-600 text-white'
          }`}
        >
          {toast.msg}
        </div>
      )}

      {/* Tabela de membros */}
      <div className="overflow-hidden rounded-lg border border-[var(--border)] bg-[var(--surface)]">
        {loadingMembers ? (
          <div className="space-y-2 px-4 py-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-8 animate-pulse rounded bg-[color-mix(in_oklch,var(--fg)_6%,transparent)]" />
            ))}
          </div>
        ) : membersError ? (
          <p className="px-4 py-8 text-center text-sm text-red-500">
            {(membersError as ApiError).message}
          </p>
        ) : !members || members.length === 0 ? (
          <div className="flex flex-col items-center gap-3 px-4 py-12 text-center">
            <Users className="h-10 w-10 text-[var(--fg-muted)] opacity-40" />
            <p className="text-sm text-[var(--fg-muted)]">Nenhum membro cadastrado.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[color-mix(in_oklch,var(--fg)_4%,transparent)] text-left text-xs uppercase tracking-wide text-[var(--fg-muted)]">
              <tr>
                <th className="px-4 py-2 font-medium">Nome</th>
                <th className="px-4 py-2 font-medium">E-mail</th>
                <th className="px-4 py-2 font-medium">Papel</th>
                <th className="px-4 py-2 font-medium">Módulos</th>
                <th className="px-4 py-2 font-medium w-20" />
              </tr>
            </thead>
            <tbody>
              {members.map((m) => (
                <tr
                  key={m.id}
                  className="border-t border-[var(--border)] transition hover:bg-[color-mix(in_oklch,var(--fg)_3%,transparent)]"
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[var(--accent)] text-[10px] font-semibold text-[var(--accent-fg)]">
                        {initials(m.user.name)}
                      </span>
                      <span className="font-medium text-[var(--fg)]">{m.user.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-[var(--fg-muted)]">{m.user.email}</td>
                  <td className="px-4 py-3">{roleBadge(m)}</td>
                  <td className="px-4 py-3">{moduleChips(m)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {m.legacyRole !== 'owner' && (
                        <>
                          <button
                            type="button"
                            onClick={() => openEdit(m)}
                            className="rounded p-1.5 text-[var(--fg-muted)] hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)] hover:text-[var(--fg)]"
                            title="Editar"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleRemove(m)}
                            className="rounded p-1.5 text-[var(--fg-muted)] hover:bg-red-500/10 hover:text-red-500"
                            title="Remover"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Convites pendentes */}
      {pendingInvites.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-sm font-semibold text-[var(--fg)]">
            Convites pendentes ({pendingInvites.length})
          </h2>
          <div className="overflow-hidden rounded-lg border border-[var(--border)] bg-[var(--surface)]">
            <table className="w-full text-sm">
              <thead className="bg-[color-mix(in_oklch,var(--fg)_4%,transparent)] text-left text-xs uppercase tracking-wide text-[var(--fg-muted)]">
                <tr>
                  <th className="px-4 py-2 font-medium">E-mail</th>
                  <th className="px-4 py-2 font-medium">Papel</th>
                  <th className="px-4 py-2 font-medium">Expira em</th>
                  <th className="px-4 py-2 font-medium w-20" />
                </tr>
              </thead>
              <tbody>
                {pendingInvites.map((inv) => (
                  <tr
                    key={inv.id}
                    className="border-t border-[var(--border)]"
                  >
                    <td className="px-4 py-3 text-[var(--fg)]">{inv.email}</td>
                    <td className="px-4 py-3 text-[var(--fg-muted)]">
                      {ROLE_LABEL[inv.legacyRole] ?? inv.legacyRole}
                    </td>
                    <td className="px-4 py-3 text-[var(--fg-muted)]">
                      {new Date(inv.expiresAt).toLocaleDateString('pt-BR')}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => handleRevokeInvite(inv)}
                        className="text-xs text-red-500 hover:underline"
                      >
                        Revogar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <UserFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        editing={editing}
        tenantId={tenantId ?? ''}
        allMembers={members ?? []}
        onDone={() => {
          queryClient.invalidateQueries({ queryKey: ['tenants', tenantId, 'members'] });
          showToast(editing ? 'Usuário atualizado' : 'Usuário criado');
        }}
      />
    </div>
  );
}
